import { useState } from "react";
import { useInterviews, useRecordInterview, useRubrics, type Rubric } from "@/app/questionQueries";
import { plural } from "@/lib/plural";
import { Button } from "@/ui/Button";

const SCALE = [0, 2, 4, 6, 8, 10];

/** Мок-интервью по рубрике (SP4 §4): оценки 0–10 по критериям с весами,
 *  итог — взвешенная сумма; история — новые сверху. */
export function Interviews({ userId }: { userId: string }) {
  const rubrics = useRubrics();
  const list = useInterviews(userId);
  const record = useRecordInterview(userId);
  const rubric: Rubric | undefined = rubrics.data?.[0];
  const [scores, setScores] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const filled = rubric ? rubric.criteria.filter((c) => scores[c.key] !== undefined).length : 0;
  const total = rubric ? weighted(rubric, scores) : 0;
  const reason = !rubric ? "рубрика ещё не загрузилась" : filled === 0 ? "поставь хотя бы одну оценку" : record.isPending ? "записываем…" : undefined;
  const submit = async () => {
    if (!rubric || reason) return;
    await record.mutateAsync({ rubric_id: rubric.id, scores, notes: notes.trim() || undefined });
    setScores({});
    setNotes("");
  };
  const items = list.data ?? [];
  return (
    <section className="rounded-md border border-bd bg-card" data-testid="interviews">
      <div className="flex items-baseline justify-between border-b border-bd px-4 py-3">
        <h2 className="m-0 font-mono text-[11px] font-normal uppercase tracking-[.06em] text-mut">мок-интервью</h2>
        <span className="font-mono text-[11px] text-mut">{items.length ? `${items.length} ${plural(items.length, "запись", "записи", "записей")}` : "записей пока нет"}</span>
      </div>
      <div className="flex flex-col gap-3 px-4 py-3.5">
        {rubric && (
          <div className="flex flex-col gap-2" data-testid="interview-form">
            <div className="font-mono text-[11px] text-ink2">рубрика «{rubric.name}» · оценка 0–10, вес критерия в скобках</div>
            {rubric.criteria.map((c) => (
              <label key={c.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-[12.5px] text-ink">
                <span>
                  {c.label} <span className="font-mono text-[11px] text-ink2">({Math.round(c.weight * 100)}%)</span>
                </span>
                <select aria-label={c.label} value={scores[c.key] ?? ""} onChange={(e) => setScores((s) => (e.target.value === "" ? omit(s, c.key) : { ...s, [c.key]: Number(e.target.value) }))} className="rounded-sm border border-bd2 bg-bg2 px-2 py-1 font-mono text-[12px] text-ink">
                  <option value="">—</option>
                  {SCALE.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <textarea aria-label="заметки к интервью" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="что получилось, что разобрать" rows={2} className="rounded-sm border border-bd2 bg-bg2 px-2.5 py-1.5 font-sans text-[12.5px] text-ink" />
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-ink2" data-testid="interview-total">
                {filled ? `итог ${total.toFixed(1)} из 10 · ${filled} из ${rubric.criteria.length}` : "итог появится по оценкам"}
              </span>
              <Button size="sm" variant="primary" disabled={!!reason} reason={reason} onClick={() => void submit()} data-testid="interview-submit">
                записать интервью
              </Button>
            </div>
            {record.error && (
              <p className="m-0 font-mono text-[11px] text-err" role="alert">
                {record.error.message}
              </p>
            )}
          </div>
        )}
        {rubrics.isError && <p className="m-0 text-[12.5px] text-err">Рубрика не загрузилась — обнови страницу.</p>}
        {list.isError && <p className="m-0 text-[12.5px] text-err">Записи не загрузились — обнови страницу; новые записи не пропадут.</p>}
        {items.length > 0 && (
          <ul className="m-0 list-none p-0" data-testid="interview-list">
            {items.map((iv) => (
              <li key={iv.id} className="flex items-baseline justify-between gap-3 border-t border-bd py-2 text-[12.5px]" data-testid="interview">
                <span className="text-ink2">
                  <span className="font-mono text-[11px]">{iv.held_on}</span> · итог <span className="font-mono text-ink">{iv.total.toFixed(1)}</span>
                  {iv.notes && <span className="text-ink2"> · {iv.notes}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function omit(s: Record<string, number>, k: string) {
  const { [k]: _drop, ...rest } = s;
  void _drop;
  return rest;
}

/** Взвешенное среднее по заполненным критериям — как Score() на сервере. */
export function weighted(r: Rubric, scores: Record<string, number>): number {
  let total = 0;
  let weight = 0;
  for (const c of r.criteria) {
    const v = scores[c.key];
    if (v !== undefined) {
      total += v * c.weight;
      weight += c.weight;
    }
  }
  return weight ? total / weight : 0;
}
